import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { encryptPayload } from "./backendEncryption";
import { getProxyEndpoint, encryptRoutingInfo, decryptResponseData } from "./encryptionUtils";
import { simpleLogout } from "./logoutUtility";

/**
 * Creates an encrypted baseQuery that encrypts request bodies and decrypts responses
 * Uses backend encryption/decryption only
 * @param {object} options - BaseQuery options
 * @param {boolean} options.forceProxy - Force proxy mode (ignore REACT_APP_USE_PROXY flag)
 * @returns {function} - Encrypted baseQuery function
 */
export const createEncryptedBaseQuery = (options = {}) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: options.baseUrl || process.env.REACT_APP_BASE_URL,
    prepareHeaders: options.prepareHeaders || ((headers, { getState }) => {
      const token = localStorage.getItem("token");
      const uid = getState()?.users?.userInfo?._id;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      if (uid) headers.set("uid", uid);
      if (!headers.get("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      return headers;
    }),
  });

  return async (args, api, extraOptions) => {
    try {
      // Check if proxy mode should be used
      // forceProxy option overrides REACT_APP_USE_PROXY flag
      const USE_PROXY = options.forceProxy !== undefined
        ? options.forceProxy
        : process.env.REACT_APP_USE_PROXY !== 'false';

      // Handle both string and object args from RTK Query
      const endpoint = typeof args === 'string' ? args : args.url;
      const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
      const body = typeof args === 'string' ? undefined : args.body;
      const params = typeof args === 'string' ? undefined : args.params;

      if (USE_PROXY) {
        // Proxy mode: Route through proxy endpoint
        const PROXY_ENDPOINT = getProxyEndpoint(endpoint);

        // Encrypt routing info (logging happens inside encryptRoutingInfo)
        const encryptedRoute = await encryptRoutingInfo({
          endpoint,
          method,
          body,
          params
        });

        // Create proxy request
        const proxyArgs = {
          url: PROXY_ENDPOINT,
          method: 'POST',
          body: {
            envelope: encryptedRoute.envelope,
            signature: encryptedRoute.signature
          }
        };

        let result = await baseQuery(proxyArgs, api, extraOptions);

        // Decrypt response if it exists
        if (result.data) {
          try {
            result.data = await decryptResponseData(result.data);
            // Log successful response
            console.log("✅ API Success (Proxy Mode)", {
              endpoint: endpoint,
              method: method,
              response: result.data,
              payload: {
                body: body,
                params: params
              },
              message: result.data?.message || result.data?.msg || result.data?.data?.message || "Success"
            });
          } catch (error) {
            console.error("❌ Decryption failed:", {
              endpoint: endpoint,
              method: method,
              error: error.message,
              payload: {
                body: body,
                params: params
              },
              data: result.data
            });
            return {
              error: {
                status: 'DECRYPTION_ERROR',
                error: error.message,
                data: result.data
              }
            };
          }
        }

        // Handle errors from backend
        if (result.error) {
          console.error("❌ API Error (Proxy Mode):", {
            endpoint: endpoint,
            method: method,
            status: result.error.status,
            error: result.error.error || result.error.data,
            payload: {
              body: body,
              params: params
            },
            fullError: result.error
          });
        }

        // Handle token expiration
        if (result.error && result.error.status === 403) {
          const state = api.getState();
          const uid = state?.users?.userInfo?._id;
          await simpleLogout(uid);
        }

        return result;
      }

      // Direct mode: Send requests directly to real APIs
      // Log the decrypted payload before encryption
      // console.log('🔓 API Request (Decrypted Form - Direct Mode):', {
      //   endpoint: endpoint,
      //   method: method,
      //   body: body,
      //   params: params
      // });

      // Encrypt the body if it exists and is not FormData
      let requestArgs = args;
      if (typeof args === 'object' && args.body && !(args.body instanceof FormData)) {
        const encryptedBody = await encryptPayload(args.body);
        requestArgs = {
          ...args,
          body: encryptedBody
        };

        // Log encrypted form
        // console.log('🔒 API Request (Encrypted Form - Direct Mode):', {
        //   endpoint: endpoint,
        //   method: method,
        //   body: typeof encryptedBody === 'object' && encryptedBody.envelope 
        //     ? { envelope: encryptedBody.envelope.substring(0, 50) + '...', signature: encryptedBody.signature.substring(0, 20) + '...' }
        //     : encryptedBody,
        //   params: params
        // });
      }

      let result = await baseQuery(requestArgs, api, extraOptions);

      // Decrypt response if it exists
      if (result.data) {
        try {
          result.data = await decryptResponseData(result.data);
          // Log successful response
          // console.log("✅ API Success (Direct Mode):", {
          //   endpoint: endpoint,
          //   method: method,
          //   response: result.data,
          //   message: result.data?.message || result.data?.msg || result.data?.data?.message || "Success"
          // });
        } catch (error) {
          console.error("❌ Decryption failed:", {
            endpoint: endpoint,
            method: method,
            error: error.message,
            payload: {
              body: body,
              params: params
            },
            data: result.data
          });
          return {
            error: {
              status: 'DECRYPTION_ERROR',
              error: error.message,
              data: result.data
            }
          };
        }
      }

      // Handle errors from backend
      if (result.error) {
        console.error("❌ API Error (Direct Mode):", {
          endpoint: endpoint,
          method: method,
          status: result.error.status,
          error: result.error.error || result.error.data,
          payload: {
            body: body,
            params: params
          },
          fullError: result.error
        });
      }

      // Handle token expiration
      if (result.error && result.error.status === 403) {
        const state = api.getState();
        const uid = state?.users?.userInfo?._id;
        await simpleLogout(uid);
      }

      return result;
    } catch (error) {
      const endpoint = typeof args === 'string' ? args : args.url;
      const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
      const body = typeof args === 'string' ? undefined : args.body;
      const params = typeof args === 'string' ? undefined : args.params;
      console.error("❌ Request error:", {
        endpoint: endpoint,
        method: method,
        error: error.message,
        payload: {
          body: body,
          params: params
        },
        stack: error.stack
      });
      return { error: { status: 'FETCH_ERROR', error: error.message } };
    }
  };
};

/**
 * Enhanced encrypted baseQuery with reauth functionality
 * This is the main export that should be used in API slices
 * Uses proxy mode by default (controlled by REACT_APP_USE_PROXY)
 * @param {string} baseUrl - Optional base URL (defaults to REACT_APP_BASE_URL)
 * @returns {function} - Encrypted baseQuery function
 */
export const encryptedBaseQueryWithReauth = (baseUrl) => {
  const url = baseUrl || process.env.REACT_APP_BASE_URL;

  return createEncryptedBaseQuery({
    baseUrl: url,
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem("token");
      const uid = getState()?.users?.userInfo?._id;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      if (uid) headers.set("uid", uid);
      if (!headers.get("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      return headers;
    },
  });
};

/**
 * Proxy baseQuery with reauth functionality
 * Always uses proxy mode (ignores REACT_APP_USE_PROXY flag)
 * Use this when you want to force proxy mode for specific APIs
 * @param {string} baseUrl - Optional base URL (defaults to REACT_APP_BASE_URL)
 * @returns {function} - Encrypted baseQuery function with forced proxy mode
 */
export const proxyBaseQueryWithReauth = (baseUrl) => {
  const url = baseUrl || process.env.REACT_APP_BASE_URL;

  return createEncryptedBaseQuery({
    baseUrl: url,
    forceProxy: true, // Force proxy mode
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem("token");
      const uid = getState()?.users?.userInfo?._id;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      if (uid) headers.set("uid", uid);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  });
};